/**
 * Getting a pattern into the editor: the clipboard, a text file read in the browser (FileReader,
 * nothing is sent), or a tiny starter. Pure helpers, so the app and the tests share them.
 */
import type { Lang } from '../i18n';

/**
 * A starting point, not a tutorial: the optional header, then a cast-on, a ribbing and a row with a
 * stated count, in the style the engine reads (checked clean by stitchcore in both languages).
 */
const STARTERS: Record<Lang, string> = {
  fr: [
    '# title: Mon patron',
    '# sizes: S, M, L',
    '# lang: fr',
    'Monter 80 (88, 96) m. Joindre en rond.',
    "Tours 1-6 : *2 m end, 2 m env*, répéter de * à * jusqu'à la fin du tour.",
    "Tour 7 : *2 m ens à l'end, 2 m end*, répéter de * à * jusqu'à la fin du tour (il reste 60 (66, 72) m).",
    '',
  ].join('\n'),
  en: [
    '# title: My pattern',
    '# sizes: S, M, L',
    '# lang: en',
    'Cast on 80 (88, 96) sts. Join in the round.',
    'Rnds 1-6: *k2, p2*, repeat to end of round.',
    'Rnd 7: *k2tog, k2*, repeat to end of round (60 (66, 72) sts).',
    '',
  ].join('\n'),
};

/** The starter in the interface language; it ends with a new line, ready for the next row. */
export function starterText(lang: Lang): string {
  return STARTERS[lang];
}

/** One newline convention (the editor's) and no byte-order mark. */
export function normalizeText(text: string): string {
  return text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
}

// ------------------------------------------------------------------ clipboard

export type ClipboardOutcome = { kind: 'text'; text: string } | { kind: 'empty' } | { kind: 'unavailable' };

interface ClipboardScope {
  isSecureContext?: boolean;
  navigator?: { clipboard?: { readText?: () => Promise<string> } };
}

/**
 * Reads the clipboard when the browser allows it (https or localhost, permission granted). Call it
 * synchronously inside the click: browsers only allow it during a user gesture. Never rejects.
 */
export function readClipboard(scope: ClipboardScope = globalThis as ClipboardScope): Promise<ClipboardOutcome> {
  const clipboard = scope.isSecureContext ? scope.navigator?.clipboard : undefined;
  if (!clipboard || typeof clipboard.readText !== 'function') return Promise.resolve({ kind: 'unavailable' });
  try {
    return clipboard.readText().then(
      (text): ClipboardOutcome => (typeof text === 'string' && text.trim() !== '' ? { kind: 'text', text: normalizeText(text) } : { kind: 'empty' }),
      (): ClipboardOutcome => ({ kind: 'unavailable' }),
    );
  } catch {
    return Promise.resolve({ kind: 'unavailable' });
  }
}

// ------------------------------------------------------------------ files

/** What the file picker offers (the drop zone accepts the same). */
export const FILE_ACCEPT = '.txt,.md,text/plain';
/** A pattern is a few kilobytes; beyond this it is not one (and would slow the check down). */
export const MAX_FILE_BYTES = 1_000_000;

const TEXT_EXTENSIONS = new Set(['txt', 'md', 'markdown', 'text']);
/** Control characters a text never has in a row (the test CodeMirror uses for dropped files). */
// eslint-disable-next-line no-control-regex
const BINARY = /[\x00-\x08\x0e-\x1f]{2}/;

export type FileProblem = 'pdf' | 'unsupported' | 'tooBig' | 'empty' | 'readFailed';
export type FileOutcome = { kind: 'text'; text: string; name: string } | { kind: FileProblem; name: string };

/** From the name and type only: plain text, a PDF (to copy from), or something else. */
export function fileKind(file: { name: string; type: string }): 'text' | 'pdf' | 'unsupported' {
  const dot = file.name.lastIndexOf('.');
  const ext = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : '';
  const type = file.type.toLowerCase();
  if (ext === 'pdf' || type === 'application/pdf') return 'pdf';
  if (TEXT_EXTENSIONS.has(ext) || type.startsWith('text/')) return 'text';
  // No extension and no type (a file saved without one): read it, the content decides.
  if (!ext && !type) return 'text';
  return 'unsupported';
}

export interface TextReader {
  onload: ((this: never, ev: never) => unknown) | null;
  onerror: ((this: never, ev: never) => unknown) | null;
  result: string | ArrayBuffer | null;
  readAsText(blob: Blob, encoding?: string): void;
}
export type TextReaderCtor = new () => TextReader;

function readAs(file: Blob, encoding: string, Reader: TextReaderCtor): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new Reader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsText(file, encoding);
  });
}

/**
 * Reads a pattern file on this device (FileReader: nothing leaves the browser). UTF-8 first; an
 * old Windows file with accents (windows-1252) is read again in that encoding. A PDF, a word
 * processor file or an image is refused with a reason the interface explains.
 */
export async function readPatternFile(
  file: File,
  Reader: TextReaderCtor = (globalThis as unknown as { FileReader: TextReaderCtor }).FileReader,
): Promise<FileOutcome> {
  const name = file.name;
  const kind = fileKind(file);
  if (kind !== 'text') return { kind, name };
  if (file.size > MAX_FILE_BYTES) return { kind: 'tooBig', name };
  if (file.size === 0) return { kind: 'empty', name };
  let text: string;
  try {
    text = await readAs(file, 'utf-8', Reader);
    if (text.includes('\uFFFD')) text = await readAs(file, 'windows-1252', Reader).catch(() => text);
  } catch {
    return { kind: 'readFailed', name };
  }
  if (text.startsWith('%PDF-')) return { kind: 'pdf', name };
  if (BINARY.test(text)) return { kind: 'unsupported', name };
  text = normalizeText(text);
  if (text.trim() === '') return { kind: 'empty', name };
  return { kind: 'text', text, name };
}

/** A finger rather than a mouse: « Ctrl+V » means nothing there, a long press does. */
export function prefersTouch(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/** True when a drag carries files from the computer (not text moved inside the editor). */
export function dragHasFiles(dt: { types?: ArrayLike<string> | null } | null | undefined): boolean {
  return !!dt?.types && Array.from(dt.types).includes('Files');
}
